import { PCA_RASCUNHOS_STORAGE_KEY } from 'src/app/core/models/pca.model';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as jwt_decode from 'jwt-decode';
import { Usuario } from 'src/app/core/models/usuario.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { HttpOptions } from 'src/app/core/models/http-options.model';
import { QueryParams } from 'src/app/core/models/query-params.model';
import { catchError, defaultIfEmpty, defer, map, Observable, of, take } from 'rxjs';
import { AppLocalStorageService } from '../../shared/services/app-local-storage.service';
import { SpinnerService } from 'src/app/shared/components/spinner/spinner.service';
import { UnidadeBM } from 'src/app/core/models/unidadebm.model';
import { SgrResponse } from 'src/app/core/models/response.model';
import { HttpParameterCodec } from '@angular/common/http';
import { NullSafe } from 'src/app/core/types/null-safe.type';
import { RascunhoItensCompraService } from 'src/app/shared/services/rascunho-itens-compra.service';
import { PopupService } from 'src/app/shared/services/popup.service';

export class CustomHttpParamEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(decodeURIComponent(key));
  }
  encodeValue(value: string): string {
    return encodeURIComponent(decodeURIComponent(value));
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  static readonly TOKEN_ADMINISTRADOR_KEY = 'tokenAdministrador';
  // Rotas fora da área autenticada: não exigem token de funcionário válido nem disparam "sessão expirou".
  private static readonly ROTAS_PUBLICAS = ['/login', '/cadastro-usuario', '/definir-senha', '/externo'];

  private url: string = environment.API;
  private urlExterno: string = environment.API_EXTERNO;
  private redirecionandoSessaoInvalida: boolean = false;

  constructor(
    private httpClient: HttpClient,
    private localStorageService: AppLocalStorageService,
    private router: Router,
    private spinnerService: SpinnerService,
    private popupService: PopupService
  ) {}

  // ? MÉTODOS LOCAIS

  get token(): string | null {
    return this.localStorageService.getItem('token');
  }

  get tokenDecoded(): string {
    return jwt_decode.jwtDecode(this.token!);
  }

  /** Sessão de usuário externo (ex.: defensor de PAD) — independente do token de funcionário. */
  get tokenExterno(): string | null {
    return this.localStorageService.getItem('tokenExterno');
  }

  limparSessaoExterna(): void {
    this.localStorageService.removeItem('tokenExterno');
  }

  /** Indica se a sessão atual veio de um "Acessar Como" e há token do admin para restaurar. */
  isAcessandoComo(): boolean {
    return this.localStorageService.hasKey(AuthService.TOKEN_ADMINISTRADOR_KEY);
  }

  /**
   * Registra o retorno no WS, ainda com o token da sessão do "Acessar Como", e restaura a sessão do administrador.
   * @returns false se não houver token válido para restaurar ou se o retorno não puder ser registrado
   */
  voltarAoAdministrador(urlRedirect?: string): Observable<boolean> {
    return defer(() => {
      const tokenAdmin = this.localStorageService.getItem(AuthService.TOKEN_ADMINISTRADOR_KEY);
      if (!tokenAdmin || !this.isJwtNotExpired(tokenAdmin)) {
        this.localStorageService.removeItem(AuthService.TOKEN_ADMINISTRADOR_KEY);
        return of(false);
      }

      const options = this.setHttpOptions({ spinnerDelay: 500 });
      return this.httpClient.post<SgrResponse>(this.url + '/login/voltaradministrador', null, options).pipe(
        take(1),
        map(response => !response.error && this.restaurarSessaoAdministrador(tokenAdmin, urlRedirect)),
        defaultIfEmpty(false),
        catchError(() => of(false))
      );
    });
  }

  private restaurarSessaoAdministrador(tokenAdmin: string, urlRedirect?: string): boolean {
    this.localStorageService.clear({ keysToPreserve: ['sidenav_state', 'unidadesSelecionadas', PCA_RASCUNHOS_STORAGE_KEY, RascunhoItensCompraService.STORAGE_KEY, RascunhoItensCompraService.LEGACY_STORAGE_KEY] });
    this.localStorageService.setItem('token', tokenAdmin);

    const usuario = this.usuarioLogado();
    if (!usuario) return false;
    const primeiroNome = usuario?.NOME?.split(' ')[0] ?? '';
    this.localStorageService.setItem('saudacao', `Seja bem‑vindo ${primeiroNome}!`);
    this.redirecionarAposTrocaUsuario(urlRedirect);
    return true;
  }

  isTokenValid(): boolean {
    return this.isJwtNotExpired(this.token);
  }

  /** Rota atual não exige sessão de funcionário (login, cadastro externo, definição de senha, área externa). */
  isRotaPublica(): boolean {
    return AuthService.ROTAS_PUBLICAS.some(rota => this.router.url.includes(rota));
  }

  private isJwtNotExpired(token: string | null): boolean {
    if (!token) return false;

    try {
      const { exp } = jwt_decode.jwtDecode<{ exp: number }>(token);
      return Date.now() < exp * 1000; // ainda não venceu
    } catch {
      return false;
    }
  }

  // --- REDIRECT ---------------------------------------------------
  saveRedirect(url: string): void {
    this.localStorageService.setItem('redirect', url);
  }

  getRedirect(): string | null {
    return this.localStorageService.getItem('redirect');
  }

  clearRedirect(): void {
    this.localStorageService.removeItem('redirect');
  }

  setHttpOptions(params: { queryParams?: QueryParams; spinnerDelay?: number; spinnerOff?: boolean }): HttpOptions {
    let allParams = {
      ...params?.queryParams,
      spinnerDelay: params?.spinnerDelay ?? 0,
      spinnerOff: params?.spinnerOff ?? false,
    };
    let options: HttpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.token,
        'Content-Type': 'application/json',
      }),
      params: new HttpParams({ fromObject: allParams, encoder: new CustomHttpParamEncoder() }),
    };
    return options;
  }

  /** Igual a setHttpOptions, mas autentica com o token de usuário externo em vez do de funcionário. */
  setHttpOptionsExterno(params: { queryParams?: QueryParams; spinnerDelay?: number; spinnerOff?: boolean }): HttpOptions {
    let allParams = {
      ...params?.queryParams,
      spinnerDelay: params?.spinnerDelay ?? 0,
      spinnerOff: params?.spinnerOff ?? false,
    };
    let options: HttpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.tokenExterno,
        'Content-Type': 'application/json',
      }),
      params: new HttpParams({ fromObject: allParams, encoder: new CustomHttpParamEncoder() }),
    };
    return options;
  }

  usuarioLogado(): Usuario | null {
    const token = this.token;
    if (!token) {
      this.tratarSessaoInvalida('Não foi possível validar sua sessão. Faça login novamente.');
      return null;
    }

    try {
      const payload: any = jwt_decode.jwtDecode(token);
      const userData = payload?.data?.user;

      if (!userData) {
        this.tratarSessaoInvalida('Não foi possível validar sua sessão. Faça login novamente.');
        return null;
      }

      const usuario = new Usuario(userData);

      usuario.UNIDADESELECIONADA = this.getUnidadeSelecionada(usuario) || usuario.LOTACAO[0];

      return usuario;
    } catch {
      this.tratarSessaoInvalida('Não foi possível validar sua sessão. Faça login novamente.');
      return null;
    }
  }

  tratarSessaoInvalida(mensagem: string = 'Sua sessão expirou! Faça login novamente.'): void {
    if (this.redirecionandoSessaoInvalida || this.isRotaPublica()) return;

    this.redirecionandoSessaoInvalida = true;
    this.popupService.showAlert(mensagem);
    this.saveRedirect(this.router.url);
    this.router.navigateByUrl('/login').finally(() => {
      this.redirecionandoSessaoInvalida = false;
    });
  }

  // usuarioLogado(): Usuario | null {
  //   if (!this.isTokenValid()) return null;
  //   const payload: any = jwt_decode.jwtDecode(this.token!);
  //   const userData = payload?.data?.user;
  //   if (userData) {
  //     // Instancia a classe Usuario
  //     const usuario = new Usuario(userData);
  //     const unidadeSelecionadaUsuario = this.getUnidadeSelecionada(usuario);
  //     usuario.UNIDADESELECIONADA = unidadeSelecionadaUsuario || usuario.LOTACAO[0];
  //     return usuario;
  //   }
  //   return null;
  // }

  private getUnidadeSelecionada(usuario: Usuario): NullSafe<UnidadeBM> {
    const unidadesSelecionadasStorageRaw = this.localStorageService.getItem('unidadesSelecionadas');
    const unidadesSelecionadasStorage: UnidadeSelecionada[] = unidadesSelecionadasStorageRaw ? JSON.parse(unidadesSelecionadasStorageRaw) : [];
    return usuario.LOTACAO?.find(
      unidade => unidade.CODUNIDADE === unidadesSelecionadasStorage?.find(u => u.codUsuarioLogado === usuario.CODUSUARIO)?.codUnidadeSelecionada
    );
  }

  changeUnidadeSelecionadaUsuarioLogado(codUnidade: number): Usuario | null {
    const usuario = this.usuarioLogado();
    if (!usuario) return null;

    const unidadesSelecionadasStorageRaw = this.localStorageService.getItem('unidadesSelecionadas');

    const unidadesSelecionadasStorage: UnidadeSelecionada[] = unidadesSelecionadasStorageRaw ? JSON.parse(unidadesSelecionadasStorageRaw) : [];

    const indexUnidadeUsuario = unidadesSelecionadasStorage.findIndex(unidade => unidade.codUsuarioLogado === usuario.CODUSUARIO);

    if (indexUnidadeUsuario === -1) {
      unidadesSelecionadasStorage.push({
        codUsuarioLogado: usuario.CODUSUARIO,
        codUnidadeSelecionada: codUnidade,
      });
    } else {
      unidadesSelecionadasStorage[indexUnidadeUsuario].codUnidadeSelecionada = codUnidade;
    }

    this.localStorageService.setItem('unidadesSelecionadas', JSON.stringify(unidadesSelecionadasStorage));

    return this.usuarioLogado();
  }

  // changeUnidadeSelecionadaUsuarioLogado(codUnidade: number): Usuario | null {
  //   const usuario = this.usuarioLogado();
  //   if (!usuario) return null;

  //   const unidadesSelecionadasStorageRaw = this.localStorageService.getItem('unidadesSelecionadas');
  //   let unidadesSelecionadasStorage: UnidadeSelecionada[] = unidadesSelecionadasStorageRaw ? JSON.parse(unidadesSelecionadasStorageRaw) : [];
  //   if (!unidadesSelecionadasStorage) unidadesSelecionadasStorage = [];

  //   const indexUnidadeUsuario = unidadesSelecionadasStorage.findIndex(u => u.codUsuarioLogado === usuario.CODUSUARIO);
  //   if (indexUnidadeUsuario > -1) {
  //     unidadesSelecionadasStorage[indexUnidadeUsuario].codUnidadeSelecionada = codUnidade;
  //   } else {
  //     unidadesSelecionadasStorage.push({ codUsuarioLogado: usuario.CODUSUARIO, codUnidadeSelecionada: codUnidade });
  //   }
  //   this.localStorageService.setItem('unidadesSelecionadas', JSON.stringify(unidadesSelecionadasStorage));
  //   return this.usuarioLogado();
  // }

  logout(): void {
    this.spinnerService.start();
    setTimeout(() => {
      this.router.navigateByUrl('login').then(() => {
        this.spinnerService.finish();
      });
    }, 300);
  }

  //? CHAMADAS AO BACKEND
  login(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    let endpoint = this.url + '/login';
    let options = this.setHttpOptions(params);
    return this.httpClient.post<SgrResponse>(endpoint, params.body, options);
  }

  /** Troca a sessão e recarrega a aplicação; aceita também o token de origem da Intranet. */
  acessarComo(cpf: string, urlRedirect?: string): Observable<boolean> {
    return defer(() => {
      if (!cpf?.trim()) {
        this.popupService.showError('Informe o CPF para acessar como.');
        return of(false);
      }

      const body = JSON.stringify({ cpf });
      const options = this.setHttpOptions({ spinnerDelay: 500 });
      return this.httpClient.post<SgrResponse<{ token: string }[]>>(this.url + '/login/acessarcomo', body, options).pipe(
        take(1),
        map(response => {
          const token = response.data?.[0]?.token;
          if (response.error || !token) {
            this.popupService.showError(response.msg || 'Erro ao realizar acesso!');
            return false;
          }
          return this.aplicarSessaoAcessarComo(token, urlRedirect);
        }),
        defaultIfEmpty(false),
        catchError(() => {
          this.popupService.showError('Ocorreu um erro ao solicitar acesso como.');
          return of(false);
        })
      );
    });
  }

  private aplicarSessaoAcessarComo(token: string, urlRedirect?: string): boolean {
    if (!this.isJwtNotExpired(token)) {
      this.popupService.showError('Token inválido para acesso como.');
      return false;
    }

    const payload = jwt_decode.jwtDecode<{ data?: { user?: ConstructorParameters<typeof Usuario>[0] } }>(token);
    if (typeof payload.data?.user?.NOME !== 'string' || !payload.data.user.NOME.trim() || !Array.isArray(payload.data.user.LOTACAO)) {
      this.popupService.showError('Token inválido para acesso como.');
      return false;
    }
    const usuario = new Usuario(payload.data.user);
    usuario.UNIDADESELECIONADA = this.getUnidadeSelecionada(usuario) || usuario.LOTACAO[0];
    const tokenAdministrador = this.localStorageService.getItem(AuthService.TOKEN_ADMINISTRADOR_KEY) ?? this.token;

    this.localStorageService.clear({ keysToPreserve: ['sidenav_state', 'unidadesSelecionadas', PCA_RASCUNHOS_STORAGE_KEY, RascunhoItensCompraService.STORAGE_KEY, RascunhoItensCompraService.LEGACY_STORAGE_KEY] });
    if (tokenAdministrador) this.localStorageService.setItem(AuthService.TOKEN_ADMINISTRADOR_KEY, tokenAdministrador);
    this.localStorageService.setItem('token', token);
    this.localStorageService.setItem('saudacao', `Seja bem‑vindo ${usuario.NOME.split(' ')[0]}!`);
    // O shell consulta as preferências na inicialização sob o novo token.
    this.redirecionarAposTrocaUsuario(urlRedirect);
    return true;
  }

  protected redirecionarAposTrocaUsuario(urlRedirect?: string): void {
    // A navegação do Router mantém serviços e componentes da sessão anterior em memória.
    window.location.assign(urlRedirect || '/unidade/solicitacao');
  }

  loginExterno(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<UsuarioExternoResponse> {
    let endpoint = this.urlExterno + '/wslogin';
    let options = this.setHttpOptions(params);
    return this.httpClient.post<UsuarioExternoResponse>(endpoint, params.body, options);
  }

  /** Envia ao e-mail do cadastro o link de uso único para definir a senha (primeiro acesso ou esqueci minha senha). */
  solicitarSenhaExterno(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<UsuarioExternoResponse> {
    let endpoint = this.urlExterno + '/wslogin/email-senha';
    let options = this.setHttpOptions(params);
    return this.httpClient.post<UsuarioExternoResponse>(endpoint, params.body, options);
  }

  /** Define a senha a partir do token recebido no link por e-mail. */
  definirSenhaExterno(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<UsuarioExternoResponse> {
    let endpoint = this.urlExterno + '/wslogin/alterar-senha';
    let options = this.setHttpOptions(params);
    return this.httpClient.post<UsuarioExternoResponse>(endpoint, params.body, options);
  }

  meusDadosExterno(params: { spinnerDelay?: number; spinnerOff?: boolean }): Observable<UsuarioExternoResponse> {
    let endpoint = this.urlExterno + '/wslogin/meus-dados';
    let options = this.setHttpOptionsExterno(params);
    return this.httpClient.get<UsuarioExternoResponse>(endpoint, options);
  }

  alterarSenhaExterno(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<UsuarioExternoResponse> {
    let endpoint = this.urlExterno + '/wslogin/alterar-minha-senha';
    let options = this.setHttpOptionsExterno(params);
    return this.httpClient.post<UsuarioExternoResponse>(endpoint, params.body, options);
  }

  logoutExterno(params: { spinnerDelay?: number; spinnerOff?: boolean }): Observable<UsuarioExternoResponse> {
    let endpoint = this.urlExterno + '/wslogin/logout';
    let options = this.setHttpOptionsExterno(params);
    return this.httpClient.post<UsuarioExternoResponse>(endpoint, null, options);
  }

  buscaServicosExterno(params: { spinnerDelay?: number; spinnerOff?: boolean }): Observable<ServicosExternoResponse> {
    let endpoint = this.urlExterno + '/servicos';
    let options = this.setHttpOptionsExterno(params);
    return this.httpClient.get<ServicosExternoResponse>(endpoint, options);
  }
}

/**
 * Envelope de resposta do módulo Externo do ws (AuthUsuarioExternoController) — usa "message", não "msg",
 * e "token" no nível raiz (não em "data"), diferente do SgrResponse padrão do SGR.
 */
export interface UsuarioExternoResponse {
  error: boolean;
  message: string;
  token?: string;
  codusuario?: number;
  data?: UsuarioExterno;
}

export interface UsuarioExterno {
  CODUSUARIOEXTERNO: number;
  NOME: string;
  EMAIL: string;
  CPF: string;
}

/** Envelope de ServicosUsuarioExternoController::getServicos — usa "msg", diferente do restante do módulo Externo. */
export interface ServicosExternoResponse {
  error: boolean;
  msg: string;
  data?: ServicoExterno[];
}

export interface ServicoExterno {
  CODSERVICOEXTERNO: number;
  SERVICO: string;
  DESCRICAO: string;
  PATH: string;
}

interface UnidadeSelecionada {
  codUsuarioLogado: number;
  codUnidadeSelecionada: number;
}
